/**
 * The trust grader — the tool that explains all the others by running.
 *
 * The original design animated six scripted steps over 1.5 seconds. The real thing makes **four
 * outbound GETs at a five-second timeout each**, so a scan can genuinely take twenty seconds
 * against a slow or dead host. There is no progress channel, so what this shows is an honest
 * indeterminate state plus what it is actually doing — not a fake progress bar that finishes
 * before the request does.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Card, Gate, Head, ScrollX, trackToolCompleted, trackToolStarted } from "@authoxi/ui-shared";
import { type Diff, type Scan, client, grade, readDiff, watchHost } from "../api";

const STEPS = [
  "resolving the host",
  "GET /.well-known/agent",
  "GET /.well-known/http-message-signatures-directory",
  "checking the signature on a probe request",
  "looking for an accountable human",
];

export function Grader({ compact = false }: { compact?: boolean }) {
  const [url, setUrl] = useState("https://acme-agents.io");
  const [scan, setScan] = useState<Scan | null>(null);
  const [diff, setDiff] = useState<Diff | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(0);

  const run = useCallback(async (target: string) => {
    const mine = ++inflight.current;
    setBusy(true);
    setError(null);
    setDiff(null);
    // The pair is the activation rate. Started alone counts intent; completed alone hides how
    // many people asked and got nothing.
    trackToolStarted("grader");
    const startedAt = Date.now();
    try {
      const result = await grade(target);
      if (inflight.current !== mine) return;
      setScan(result);
      // The GRADE, never the target. What somebody chose to scan is their business, and putting
      // it in an analytics vendor is a different promise from the one /privacy makes.
      trackToolCompleted("grader", `grade_${result.letter}`, {
        duration_ms: Date.now() - startedAt,
      });
      // The diff is a second request and must not block the grade. "What changed since last
      // time" is the reason to come back; failing to load it is not a reason to hide the
      // answer somebody is waiting on.
      readDiff(result.scan_id)
        .then((d) => inflight.current === mine && setDiff(d))
        .catch(() => undefined);
    } catch (e) {
      if (inflight.current !== mine) return;
      setScan(null);
      trackToolCompleted("grader", "failed", { duration_ms: Date.now() - startedAt });
      // The server answers a refused target and a bad scheme with ONE identical message, so a
      // caller cannot tell "resolves to an internal address" from "not a URL". Passing it
      // through verbatim keeps that property; rewording it here would undo it.
      setError(e instanceof Error ? e.message : "That did not work.");
    } finally {
      if (inflight.current === mine) setBusy(false);
    }
  }, []);

  return (
    <>
      {!compact && (
        <Head
          pill="Trust grader"
          title="Can your agent be identified at all?"
          lede="Paste any agent or MCP endpoint. We check whether a stranger could tell who runs it, verify what it signs, and reach a human about it. Nothing is stored against you and no account is created."
        />
      )}

      <form
        className="tool-input"
        data-testid="scan-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run(url.trim());
        }}
      >
        <input
          className="input input-mono"
          data-testid="scan-url"
          placeholder="https://api.example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button className="btn btn-go btn-lg" type="submit" disabled={busy}>
          {busy ? "Grading…" : "Grade it"}
        </button>
      </form>

      {!compact && (
        <p className="sm muted" style={{ marginTop: "calc(-1 * var(--s3))" }}>
          Try{" "}
          {["stripe.com", "example.com"].map((h, i) => (
            <span key={h}>
              {i > 0 && " · "}
              <a
                href="#"
                style={{ color: "var(--action-text)" }}
                onClick={(e) => {
                  e.preventDefault();
                  setUrl(`https://${h}`);
                  void run(`https://${h}`);
                }}
              >
                {h}
              </a>
            </span>
          ))}{" "}
          · or your own.
        </p>
      )}

      <div style={{ marginTop: "var(--s5)" }}>
        {busy && <Scanning />}
        {!busy && error && <ScanError message={error} />}
        {!busy && !error && scan && <Result scan={scan} diff={diff} />}
      </div>
    </>
  );
}

function Scanning() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <Card>
      <div className="scanning" data-testid="scanning">
        <div className="spin" />
        {STEPS.map((s) => (
          <div className="scan-line" key={s}>
            {s}
          </div>
        ))}
      </div>
      {/* Said out loud past the point where a visitor starts wondering whether it is stuck.
          Four requests at a five-second timeout is twenty seconds against a dead host, and a
          spinner with no explanation is where people close the tab. */}
      {elapsed > 6 && (
        <p className="sm muted" style={{ marginTop: "var(--s3)" }}>
          Still going — we allow each request five seconds, and a host that never answers uses
          all of it. This is the scan being honest rather than fast.
        </p>
      )}
    </Card>
  );
}

function ScanError({ message }: { message: string }) {
  return (
    <div className="panel pad-lg" data-testid="scan-error">
      <p className="label">Not scanned</p>
      <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
        {message}
      </p>
      <p className="sm muted" style={{ marginTop: "var(--s3)" }}>
        Every refusal returns the same message on purpose. Telling you <em>why</em> a target was
        refused would turn a public scanner into a network-scanning oracle.
      </p>
    </div>
  );
}

function Result({ scan, diff }: { scan: Scan; diff: Diff | null }) {
  const passed = scan.checks.filter((c) => c.passed).length;
  const tone =
    scan.letter === "A" || scan.letter === "B"
      ? "allow"
      : scan.letter === "C"
        ? "escalate"
        : "deny";

  return (
    <div className="result" data-testid="scan-result">
      <Card pad={false}>
        <div className="grade">
          <div className={`grade-letter g-${scan.letter}`} data-testid="grade-letter">
            {scan.letter}
          </div>
          <div>
            <p className="label">Agent trust grade</p>
            <h2 className="md" style={{ margin: "var(--s2) 0 0" }}>
              {scan.host}
            </h2>
            <div className="score-bar" style={{ color: `var(--${tone})` }}>
              <i style={{ width: `${scan.score}%` }} />
            </div>
            <p className="sm muted" style={{ margin: "var(--s2) 0 0" }}>
              {scan.score} / 100 · {passed} of {scan.checks.length} checks passed
            </p>
          </div>
        </div>

        {/* Driven entirely by the response. The backend returns six checks and the design
            the original design drew five — a hard-coded list would silently stop showing whichever one
            was added last, which is exactly the check somebody most needs to see. */}
        <div className="checks">
          {scan.checks.map((c) => (
            <div className="check" key={c.check_id}>
              <span className={`mk ${c.passed ? "pass" : "fail"}`}>
                {c.passed ? "PASS" : "FAIL"}
              </span>
              <div>
                <strong>{c.title}</strong>
                <p>{c.detail}</p>
                {!c.passed && c.fix && (
                  <div className="fix">
                    <strong>Fix:</strong> {c.fix}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="row wrap" style={{ marginTop: "var(--s4)" }}>
        <button
          className="btn"
          onClick={() => void navigator.clipboard?.writeText(scan.share_url)}
        >
          Copy shareable link
        </button>
        <a className="btn" href="/tools/badge">
          Get the badge
        </a>
        <a className="btn" href="/tools/leaderboard">
          See the leaderboard
        </a>
      </div>

      {diff && (
        <Card className="" pad>
          <p className="label">What changed since last time</p>
          <p className="sm" style={{ margin: "var(--s2) 0 0", color: "var(--ink-2)" }}>
            A grade says where you are. Two grades say which way you are going, which is the more
            useful of the two — so the diff is free as well.
          </p>
          <div className="hr" />
          <p className="sm" data-testid="diff-summary">
            {diff.summary}
          </p>
          {diff.changes.length > 0 && (
            <ScrollX>
              <table className="table">
                <tbody>
                  {diff.changes.map((c) => (
                    <tr key={c.name}>
                      <td className="mono">{c.name}</td>
                      <td>{c.change}</td>
                      <td className="sm muted">{c.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </Card>
      )}

      {/* The ask — after a result, one field, for something whose value they have just seen. */}
      <Gate
        client={client}
        source="grader"
        subject={scan.host}
        title="The one thing we ask for"
        body={`Grades change when your infrastructure changes. We will email you when ${scan.host}'s grade moves, and not otherwise.`}
        cta="Watch this host"
        submit={(email) => watchHost(scan.scan_id, email)}
      />
    </div>
  );
}
